import moment from 'moment'
import { isColorHex } from '../../lib/colors'

export enum DeliveryTrackingActionTypes {
  RESCHEDULE_OPEN = '@@DeliveryTracking/RESCHEDULE_OPEN',
  RESCHEDULE_CLOSE = '@@DeliveryTracking/RESCHEDULE_CLOSE',
  RESCHEDULE_REQUEST = '@@DeliveryTracking/RESCHEDULE_REQUEST',
  RESCHEDULE_REQUEST_SUCCESS = '@@DeliveryTracking/RESCHEDULE_REQUEST_SUCCESS',
  RESCHEDULE_REQUEST_FAIL = '@@DeliveryTracking/RESCHEDULE_REQUEST_FAIL'
}

export enum CountryCode {
  Unknown = '',
  Israel = 'IL',
  Russia = 'RU',
}

export enum DeliveryStatus {
  Unknown = '',
  InPreparation = 'in_prep',
  OnTheWay = 'on_the_way',
  Arriving = 'arriving',
  Completed = 'completed',
  Cancelled = 'cancelled',
  NotDelivered = 'not_delivered',
  Rejected = 'rejected'
}

export const ValidDeliveryStatuses: DeliveryStatus[] = [
  DeliveryStatus.InPreparation,
  DeliveryStatus.OnTheWay,
  DeliveryStatus.Arriving,
  DeliveryStatus.Completed,
]

export const IncompleteDeliveryStatuses: DeliveryStatus[] = [
  DeliveryStatus.Cancelled,
  DeliveryStatus.NotDelivered,
  DeliveryStatus.Rejected,
]

export interface DeliveryCourierData {
  name: string
  phone: string
  picture: string
}

export class DeliveryCourier implements DeliveryCourierData {
  name: string

  phone: string

  picture: string

  constructor(data: any) {
    this.name = data.name || ''
    this.phone = data.phone || ''
    this.picture = data.picture || ''
  }
}

export interface DeliveryLocationData {
  address: string
  lat: number
  lng: number
}

export class DeliveryLocation implements DeliveryLocationData {
  address: string

  lat: number

  lng: number

  constructor(data: any) {
    this.address = data.address || ''
    this.lat = data.lat || 0.0
    this.lng = data.lng || 0.0
  }
}

export interface DeliveryCompanyTemplateData {
  primaryColor: string
  logoUrl: string
}

export class DeliveryCompanyTemplate implements DeliveryCompanyTemplateData {
  primaryColor: string

  logoUrl: string

  constructor(data: any) {
    this.primaryColor = (data.primaryColor && isColorHex(data.primaryColor)) ? data.primaryColor : '#fdb924'
    this.logoUrl = data.logoUrl || ''
  }
}

export interface DeliveryCompanyData {
  displayName: string
  template: DeliveryCompanyTemplateData
}

export class DeliveryCompany implements DeliveryCompanyData {
  displayName: string

  template: DeliveryCompanyTemplateData

  constructor(data: any) {
    this.displayName = data.displayName || ''
    this.template = new DeliveryCompanyTemplate(data.template as any || {})
  }
}

export interface DeliveryVendorData {
  name: string
}

export class DeliveryVendor implements DeliveryVendorData {
  name: string

  constructor(data: any) {
    this.name = data.name || ''
  }
}

export interface DeliveryTrackingData {
  uuid: string
  country: CountryCode
  status: DeliveryStatus
  scheduledAt: moment.Moment
  deliverFrom: moment.Moment
  deliverUntil: moment.Moment
  inRouteFrom: moment.Moment
  willArriveAt: moment.Moment
  cancellableByRecipient: boolean
  displayIdentifier: string
  source: string
  courier: DeliveryCourierData
  dropOffLocation: DeliveryLocationData
  companyInfo: DeliveryCompanyData
  vendor: DeliveryVendorData
  downloadButtonEnabled: boolean
}

export class DeliveryData implements DeliveryTrackingData {
  uuid: string

  country: CountryCode

  status: DeliveryStatus

  scheduledAt: moment.Moment

  deliverFrom: moment.Moment

  deliverUntil: moment.Moment

  inRouteFrom: moment.Moment

  willArriveAt: moment.Moment

  cancellableByRecipient: boolean

  displayIdentifier: string

  source: string

  courier: DeliveryCourierData

  dropOffLocation: DeliveryLocationData

  companyInfo: DeliveryCompanyData

  vendor: DeliveryVendorData

  downloadButtonEnabled: boolean

  constructor(data: any) {
    this.uuid = data.uuid || ''
    this.country = data.env || CountryCode.Unknown
    this.status = data.status || DeliveryStatus.Unknown
    this.scheduledAt = moment(data.scheduledAt || '')
    this.deliverFrom = moment(data.deliverFrom || '')
    this.deliverUntil = moment(data.deliverUntil || '')
    this.inRouteFrom = moment(data.inRouteFrom || '')
    this.willArriveAt = moment(data.willArriveAt || '')
    this.cancellableByRecipient = data.cancellableByRecipient || false
    this.displayIdentifier = data.displayIdentifier || ''
    this.source = data.source || ''
    this.courier = new DeliveryCourier(data.courier as any || {})
    this.dropOffLocation = new DeliveryLocation(data.dropOffLocation as any || {})
    this.companyInfo = new DeliveryCompany(data.companyInfo as any || {})
    this.vendor = new DeliveryVendor(data.vendor as any || {})
    this.downloadButtonEnabled = data.downloadButtonEnabled
  }
}

export interface DeliveryTrackingState {
  readonly deliveryData: DeliveryTrackingData
  readonly loading: boolean
  readonly found: boolean
  readonly showReschedule: boolean
  readonly rescheduleRequested: boolean
}
